import { buildUrl } from 'config/routes'
import { useRootStore } from 'hooks/rootStore'
import type { QueryParams } from 'mobx-router'
import {
  forwardRef,
  useCallback,
  type ComponentPropsWithoutRef,
  type MouseEvent,
  type ReactNode,
} from 'react'

type InternalLinkProps = {
  /** Route name from routes config */
  to: string
  /** Path parameters (e.g., { slideDeckId: '123' }) */
  params?: QueryParams
  /** Query string parameters */
  queryParams?: QueryParams
  children: ReactNode
  allowPropagation?: boolean
} & Omit<ComponentPropsWithoutRef<'a'>, 'href'>

// forwardRef enables BreakoutTooltip (and other ref-cloning wrappers) to
// anchor to the underlying <a> — removing it makes tooltips silently fail.
export const InternalLink = forwardRef<HTMLAnchorElement, InternalLinkProps>(
  function InternalLink(
    { to, params = {}, queryParams = {}, children, onClick, ...rest },
    ref
  ) {
    const rootStore = useRootStore()

    const href = buildUrl(to, params, queryParams)

    const handleClick = useCallback(
      (e: MouseEvent<HTMLAnchorElement>) => {
        if (!rest.allowPropagation) {
          e.stopPropagation()
        }
        if (onClick) {
          onClick(e)
          if (e.defaultPrevented) return
        }
        // Allow cmd+click (Mac) / ctrl+click (Windows) to open in new tab
        if (e.metaKey || e.ctrlKey) {
          // pass - allow the default browser behavior to open the link in a new tab
        } else {
          e.preventDefault()
          rootStore.navigateTo(to, params, queryParams)
        }
      },
      [onClick, rest.allowPropagation, rootStore, to, params, queryParams]
    )

    return (
      <a ref={ref} href={href} onClick={handleClick} {...rest}>
        {children}
      </a>
    )
  }
)
